import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "../../../../../stores/RootStore";
import ServiceStore from "../../../../../stores/ServiceStore";
import {
  Job,
  ProductionOrder,
  Account,
  Contact,
  Opportunity,
  Activity
} from "types/common/ServiceStoreTypes";
import { JobsListStore } from "../../ProjectsList.Store";

// Type alias for backward compatibility - Job is now the consolidated entity
type Project = Job;

/**
 * Store for managing job detail view state and operations
 */
export class JobDetailStore {
  // Observables
  loading = false;
  error: string | null = null;
  activeTab = 0;

  // Constructor
  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);
  }

  // Actions
  /**
   * Set the active tab index
   * @param tabIndex - The tab index to set as active
   */
  setActiveTab(tabIndex: number) {
    this.activeTab = tabIndex;
  }

  /**
   * Clear error state
   */
  clearError() {
    this.error = null;
  }

  /**
   * Set loading state
   * @param loading - Loading state
   */
  private setLoading(loading: boolean) {
    this.loading = loading;
  }

  /**
   * Set error state
   * @param error - Error message or null to clear
   */
  private setError(error: string | null) {
    this.error = error;
  }

  // Async Actions
  /**
   * Load job details and related data
   * @param jobId - The ID of the job to load
   */
  async loadJob(jobId: number) {
    this.setLoading(true);
    this.setError(null);

    try {
      // First load all base entities from ServiceStores to ensure relational data is available
      await Promise.all([
        // Load all base entities without queries to populate stores
        this.jobStore.find(),
        this.customersStore.find(),
        this.contactsStore.find(),
        this.opportunitiesStore.find(),
        this.activitiesStore.find(),
        this.bidsStore ? this.bidsStore.find() : Promise.resolve(),
        this.productionOrdersStore ? this.productionOrdersStore.find() : Promise.resolve(),
      ]);

      // Then load specific job details
      await this.jobStore.get(jobId);

      // Load related data if job exists
      const job = this.project;
      if (job) {
        await Promise.all([
          job.customerId ? this.customersStore.get(job.customerId) : Promise.resolve(),
          this.loadContacts(jobId),
          this.loadOpportunities(jobId),
          this.loadActivities(jobId),
        ]);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to load job";
      console.error("Failed to load job details:", { jobId, error });
      runInAction(() => {
        this.setError(errorMessage);
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  }

  /**
   * Load contacts for the project (triggers service store loading)
   * @param projectId - The ID of the project to load contacts for
   */
  async loadContacts(projectId: number) {
    try {
      const project = this.project;
      if (project?.customerId) {
        await this.contactsStore.find({
          query: {
            customerId: project.customerId,
            $sort: { lastName: 1 },
          },
        });
      }
    } catch (error) {
      console.error("Failed to load contacts:", { projectId, error });
    }
  }

  /**
   * Load opportunities for the project (triggers service store loading)
   * @param projectId - The ID of the project to load opportunities for
   */
  async loadOpportunities(projectId: number) {
    try {
      const project = this.project;
      if (project?.customerId) {
        await this.opportunitiesStore.find({
          query: {
            customerId: project.customerId,
            $sort: { estimatedValue: -1 },
          },
        });
      }
    } catch (error) {
      console.error("Failed to load opportunities:", { projectId, error });
    }
  }

  /**
   * Load activities for the project (triggers service store loading)
   * @param projectId - The ID of the project to load activities for
   */
  async loadActivities(projectId: number) {
    try {
      await this.activitiesStore.find({
        query: {
          jobId: projectId,
          $sort: { createdAt: -1 },
          $limit: 50,
        },
      });
    } catch (error) {
      console.error("Failed to load activities:", { projectId, error });
    }
  }

  /**
   * Update project information
   * @param updates - Partial project data to update
   * @returns Updated project data
   */
  async updateProject(updates: Partial<Project>) {
    const project = this.project;
    if (!project) return;

    try {
      const updated = (await this.jobStore.patch(
        project.id,
        updates
      )) as unknown as Project;

      return updated;
    } catch (error) {
      console.error("Failed to update project:", {
        projectId: project.id,
        updates,
        error,
      });
      runInAction(() => {
        this.setError(
          error instanceof Error ? error.message : "Failed to update project"
        );
      });
      throw error;
    }
  }

  /**
   * Delete the current project
   */
  async deleteProject() {
    const project = this.project;
    if (!project) return;

    const projectId = project.id;

    try {
      await this.jobStore.remove(projectId);
    } catch (error) {
      console.error("Failed to delete project:", { projectId, error });
      runInAction(() => {
        this.setError(
          error instanceof Error ? error.message : "Failed to delete project"
        );
      });
      throw error;
    }
  }

  /**
   * Create a new contact for this project
   * @param contactData - Partial contact data to create
   * @returns Created contact
   */
  async createContact(contactData: Partial<Contact>) {
    const project = this.project;
    if (!project) return;

    try {
      const contact = (await this.contactsStore.create({
        ...contactData,
        accountId: project.accountId,
      })) as unknown as Contact;

      return contact;
    } catch (error) {
      console.error("Failed to create contact:", {
        projectId: project.id,
        contactData,
        error,
      });
      throw error;
    }
  }

  /**
   * Create a new opportunity for this project
   * @param opportunityData - Partial opportunity data to create
   * @returns Created opportunity
   */
  async createOpportunity(opportunityData: Partial<Opportunity>) {
    const project = this.project;
    if (!project) return;

    try {
      const opportunity = (await this.opportunitiesStore.create({
        ...opportunityData,
        accountId: project.accountId,
      })) as unknown as Opportunity;

      return opportunity;
    } catch (error) {
      console.error("Failed to create opportunity:", {
        projectId: project.id,
        opportunityData,
        error,
      });
      throw error;
    }
  }

  /**
   * Create a new activity for this project
   * @param activityData - Partial activity data to create
   * @returns Created activity
   */
  async createActivity(activityData: Partial<Activity>) {
    const project = this.project;
    if (!project) return;

    try {
      const activity = (await this.activitiesStore.create({
        ...activityData,
        projectId: project.id,
        accountId: project.accountId,
        CreatedAt: activityData.createdAt || new Date().toISOString(),
      })) as unknown as Activity;

      return activity;
    } catch (error) {
      console.error("Failed to create activity:", {
        projectId: project.id,
        activityData,
        error,
      });
      throw error;
    }
  }

  // Computed values
  /**
   * Service store accessor for jobs
   */
  get jobStore(): ServiceStore {
    return this.rootStore.jobStore as ServiceStore;
  }

  /**
   * Service store accessor for customers
   */
  get customersStore(): ServiceStore {
    return this.rootStore.customersStore as ServiceStore;
  }

  /**
   * Service store accessor for contacts
   */
  get contactsStore(): ServiceStore {
    return this.rootStore.contactsStore as ServiceStore;
  }

  /**
   * Service store accessor for opportunities
   */
  get opportunitiesStore(): ServiceStore {
    return this.rootStore.opportunitiesStore as ServiceStore;
  }

  /**
   * Service store accessor for activities
   */
  get activitiesStore(): ServiceStore {
    return this.rootStore.activitiesStore as ServiceStore;
  }

  /**
   * Service store accessor for bids (if exists)
   */
  get bidsStore(): ServiceStore | undefined {
    return this.rootStore.bidsStore as ServiceStore | undefined;
  }

  /**
   * Service store accessor for production orders (if exists)
   */
  get productionOrdersStore(): ServiceStore | undefined {
    return this.rootStore.productionOrdersStore as ServiceStore | undefined;
  }


  /**
   * Get the current project from dialog context or service store
   */
  get project(): Project | null {
    const dialogContext = this.rootStore.routerStore?.queryParams.dialogContext;
    if (!dialogContext) return null;

    const projectId = Number(dialogContext);
    if (!projectId) return null;

    // Get project from service store entities
    const projects = this.jobStore.entities as unknown as Project[];
    return projects.find(p => p.id === projectId) || null;
  }

  /**
   * Get the customer associated with the current project
   */
  get customer(): Account | null {
    const project = this.project;
    if (!project?.customerId) return null;

    // Get customer from service store entities
    const customers = this.customersStore.entities as unknown as Account[];
    return customers.find(c => c.id === project.customerId) || null;
  }

  /**
   * Get contacts associated with the current project
   */
  get contacts(): Contact[] {
    const project = this.project;
    if (!project) return [];

    // Get contacts from service store entities
    const contacts = this.contactsStore.entities as unknown as Contact[];
    return contacts.filter(c => c.customerId === project.customerId) || [];
  }

  /**
   * Get opportunities associated with the current project
   */
  get opportunities(): Opportunity[] {
    const project = this.project;
    if (!project) return [];

    // Get opportunities from service store entities
    const opportunities = this.opportunitiesStore.entities as unknown as Opportunity[];
    return opportunities.filter(o => o.customerId === project.customerId) || [];
  }

  /**
   * Get activities associated with the current project
   */
  get activities(): Activity[] {
    const project = this.project;
    if (!project) return [];

    // Get activities from service store entities
    const activities = this.activitiesStore.entities as unknown as Activity[];
    return activities.filter(a =>
      a.jobId === project.id
    ) || [];
  }

  /**
   * Get bids associated with the current project
   */
  get bids(): unknown[] {
    const project = this.project;
    if (!project || !this.bidsStore) return [];

    // Get bids from service store entities if bids store exists
    const bids = this.bidsStore.entities as unknown as any[];
    return bids.filter(b => b.jobId === project.id) || [];
  }
  /**
   * Whether the project is currently active
   */
  get isActive(): boolean {
    return this.project?.stage === "Active";
  }

  /**
   * Project progress percentage based on progress field
   */
  get progressPercentage(): number {
    const project = this.project;
    if (!project) return 0;

    // Use the progress field from the Job interface (0-100)
    return project.progress || 0;
  }

  /**
   * Budget variance percentage
   */
  get budgetVariance(): number {
    const project = this.project;
    if (!project) return 0;

    if (project.budget && project.actualCost) {
      return (
        ((project.actualCost - project.budget) /
          project.budget) *
        100
      );
    }

    return 0;
  }

  /**
   * Most recent activities (limited to 10)
   */
  get recentActivities(): Activity[] {
    return this.activities.slice(0, 10);
  }

  /**
   * Total value of all opportunities for this project
   */
  get totalOpportunityValue(): number {
    return this.opportunities.reduce(
      (sum, opp) => sum + (opp.estimatedValue || 0),
      0
    );
  }

  /**
   * List of open opportunities (not closed won/lost)
   */
  get openOpportunities(): Opportunity[] {
    return this.opportunities.filter((opp: any) =>
      [
        "Qualification",
        "Needs Analysis",
        "Value Proposition",
        "Proposal/Price Quote",
      ].includes(opp.salesStage || "")
    );
  }

  /**
   * Primary contact for this project
   */
  get primaryContact(): Contact | undefined {
    return (
      this.contacts.find((contact: any) => contact.isPrimaryContact) ||
      this.contacts[0]
    );
  }

  /**
   * Whether the project data is currently being loaded
   */
  get isLoading(): boolean {
    return this.loading;
  }

  /**
   * Whether there is an error state
   */
  get hasError(): boolean {
    return this.error !== null;
  }

  /**
   * Whether project data is available
   */
  get hasProject(): boolean {
    return this.project !== null;
  }

  /**
   * Number of contacts associated with this project
   */
  get contactCount(): number {
    return this.contacts.length;
  }

  /**
   * Number of opportunities associated with this project
   */
  get opportunityCount(): number {
    return this.opportunities.length;
  }

  /**
   * Number of activities associated with this project
   */
  get activityCount(): number {
    return this.activities.length;
  }

  /**
   * Number of bids associated with this project
   */
  get bidCount(): number {
    return this.bids.length;
  }

  // Additional computed properties for UI compatibility
  /**
   * Number of completed tasks (placeholder)
   */
  get completedTasksCount(): number {
    // Placeholder - would need actual task data
    return 0;
  }

  /**
   * Total number of tasks (placeholder)
   */
  get totalTasksCount(): number {
    // Placeholder - would need actual task data
    return 0;
  }

  /**
   * Task completion percentage
   */
  get taskCompletionPercentage(): number {
    if (this.totalTasksCount === 0) return 0;
    return Math.round((this.completedTasksCount / this.totalTasksCount) * 100);
  }

  /**
   * Upcoming milestones (placeholder)
   */
  get upcomingMilestones(): unknown[] {
    // Placeholder - would need actual milestone data
    return [];
  }

  /**
   * Project tasks (placeholder)
   */
  get tasks(): unknown[] {
    // Placeholder - would need actual task data
    return [];
  }

  /**
   * Project milestones (placeholder)
   */
  get milestones(): unknown[] {
    // Placeholder - would need actual milestone data
    return [];
  }

  /**
   * Get the project from dialog context query parameter
   */
  get dialogProject(): Project | null {
    const projectsListStore = this.rootStore
      .projectsListStore as JobsListStore;
    const dialogContext = this.rootStore.routerStore?.queryParams.dialogContext;

    if (!dialogContext) return null;

    const projectId = Number(dialogContext);
    if (!projectId || !projectsListStore?.jobs) {
      return null;
    }

    return (
      projectsListStore.jobs.find(
        (job: Job) => job.id === projectId
      ) || null
    );
  }  /**
   * Get the specific opportunity this project was created from
   */
  get opportunity(): Opportunity | null {
    const project = this.project;
    if (!project?.opportunityId) return null;

    // Get the specific opportunity from service store entities
    const opportunities = this.opportunitiesStore.entities as unknown as Opportunity[];
    return opportunities.find(o => o.opportunityId === project.opportunityId) || null;
  }

  /**
   * Get the bid that was awarded and led to this project
   */
  get relatedBid(): unknown | null {
    const project = this.project;
    if (!project?.opportunityId || !this.bidsStore) return null;

    // Get bids from service store entities
    const bids = this.bidsStore.entities as unknown as Array<{ bidId: number, opportunityId?: number, status?: string }>;
    // Find awarded bid for the same opportunity
    return bids.find(b =>
      b.opportunityId === project.opportunityId &&
      b.status === "Awarded"
    ) || null;
  }

  /**
   * Get the production order that was created when this project moved to "In Progress"
   */
  get productionOrder(): ProductionOrder | null {
    const project = this.project;
    if (!project?.id || !this.productionOrdersStore) return null;

    // Get production orders from service store entities
    const productionOrders = this.productionOrdersStore.entities as unknown as ProductionOrder[];
    // Find production order for this project (using jobId)
    return productionOrders.find(po => po.jobId === project.id) || null;
  }

  /**
   * Public accessor for router store to check dialog context
   */
  get routerStore() {
    return this.rootStore.routerStore;
  }
}

export default JobDetailStore;

// Export with old name for backward compatibility
export { JobDetailStore as ProjectDetailStore };
